// /* eslint-disable no-unused-vars */
import {Form, Input, Switch} from 'antd';
import v2 from '../../v2/instrumentV2.module.scss';
import {FormCard, spans} from '../../v2/FormCard';
import {InstrumentActions} from '../../v2/InstrumentActions';
import {KEY_FACTS_MOCK, DISCLOSURES_MOCK} from '../../v2/devPrefill';
import {useRemainingFields} from '../../v2/useRemainingFields';
import FadeEffect from 'components/animation/fadeEffect';
import {RichTextEditor} from 'components/richTextEditor';
// import UploadImage from 'components/Startup/profile/companyImage';
import {useEffect} from 'react';
// import {Button} from '@dabafinance/react-components';
// import {UploadVideoToBucket} from 'pages/campaigns/new/screens/legalDocument/uploadDocument';
// import {toast} from 'react-toastify';
import deleteIcon from 'images/delete.svg';
import plusIcon from 'images/plus-circle.png';
import {NavTabsEnum} from 'utils/constants';
import {useSelector} from 'react-redux';
import {makeSelectTabOperations} from 'redux/store/auth';
// import {BigPlayButton, Player} from 'video-react';

/**
 * Whether rich-text copy carries any readable text.
 *
 * The statement editor emits HTML, so "empty" is `<p><br></p>` rather than ''. Strip the tags
 * (and the &nbsp; a stray space leaves behind) before deciding, or a blank disclosure passes
 * validation and is persisted as markup with nothing in it.
 */
const htmlHasText = value =>
  !!String(value || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();

export const BaseOverview = ({onSubmit, existingData}) => {
  const [form] = Form.useForm();

  const pageOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.assetClasses),
  );

  // Disclosures are deliberately NOT counted as outstanding: an instrument can legitimately
  // carry none, and a permanent "1 remaining" on the action bar trains operators to ignore it.
  // Both cards on this step count toward the sticky "what's still outstanding" bar —
  // listing only keyFacts told an operator the step was done with no disclosures on it.
  const [remaining, onValuesChange] = useRemainingFields(
    form,
    ['keyFacts', 'disclosures'],
    existingData,
  );

  useEffect(() => {
    if (existingData) {
      form.setFieldsValue({
        ...existingData,
      });
    }
  }, [existingData]);

  return (
    <div>
      <div className={v2.bodyHead}>
        <h2>Key facts and disclosures</h2>
        <p>
          The headline figures shown against this instrument on the mobile app,
          and the regulatory statements shown alongside them.
        </p>
      </div>

      <Form
        onFinish={values =>
          onSubmit({
            ...values,
            keyFacts: values?.keyFacts?.map(data => ({
              name: data?.name,
              value: data?.value,
            })),
            disclosures: values?.disclosures?.map(data => ({
              title: data?.title,
              // Send nothing rather than empty markup — `<p><br></p>` would render as a
              // blank line on every client that treats this as HTML.
              description: htmlHasText(data?.description)
                ? data?.description
                : undefined,
              // The API defaults an omitted flag to true; send it explicitly so an
              // operator switching it OFF actually clears it.
              mandatory: data?.mandatory !== false,
            })),
          })
        }
        form={form}
        onValuesChange={onValuesChange}
        layout="vertical">
        <FormCard
          title="Key facts"
          description="Short label and value pairs — coupon, tenor, rating, whatever an investor scans first">
          <Form.List name="keyFacts">
            {(fields, {add, remove}) => (
              <div className={v2.rows}>
                {fields?.map(({key, name}) => (
                  <FadeEffect key={key}>
                    <div className={v2.row}>
                      <div className={spans.third}>
                        <Form.Item
                          rules={[{required: true, message: 'Required'}]}
                          name={[name, 'name']}
                          label="Label">
                          <Input placeholder="Coupon" />
                        </Form.Item>
                      </div>
                      <div className={spans.twoThirds}>
                        <Form.Item
                          rules={[{required: true, message: 'Required'}]}
                          name={[name, 'value']}
                          label="Value">
                          <Input placeholder="4.5% annual" />
                        </Form.Item>
                      </div>
                      <div className={v2.rowDelete}>
                        <img
                          src={deleteIcon}
                          alt="Remove fact"
                          onClick={() => remove(name)}
                        />
                      </div>
                    </div>
                  </FadeEffect>
                ))}
                <button type="button" onClick={add} className={v2.addRow}>
                  <img src={plusIcon} alt="" />
                  <span>Add key fact</span>
                </button>
              </div>
            )}
          </Form.List>
        </FormCard>

        <FormCard
          title="Disclosures"
          description="Regulatory and risk statements shown on the instrument. Compulsory ones are flagged to the investor; switch that off for anything advisory">
          <Form.List name="disclosures">
            {(fields, {add, remove}) => (
              <div className={v2.rows}>
                {fields?.map(({key, name}) => (
                  <FadeEffect key={key}>
                    <div className={v2.row}>
                      <div className={spans.third}>
                        <Form.Item
                          rules={[{required: true, message: 'Required'}]}
                          name={[name, 'title']}
                          label="Title">
                          <Input placeholder="Capital at risk" />
                        </Form.Item>
                      </div>
                      <div className={spans.twoThirds}>
                        <Form.Item
                          rules={[
                            {
                              // Quill stores an empty editor as `<p><br></p>`, which is
                              // truthy — a plain `required` rule would pass on blank copy.
                              // async + throw rather than an explicit rejected promise:
                              // `Promise` is not in this project's eslint env, and every
                              // other rule of this shape here carries a no-undef disable.
                              validator: async (_, value) => {
                                if (!htmlHasText(value)) {
                                  throw new Error('Required');
                                }
                              },
                            },
                          ]}
                          name={[name, 'description']}
                          label="Statement">
                          <RichTextEditor
                            className={v2.rowEditor}
                            placeholder="The value of your investment can fall as well as rise."
                          />
                        </Form.Item>
                      </div>
                      {/* Needs a span: `.row` is a 12-column grid and the
                          two fields above it already fill all 12, so without
                          one this lands in the next row at 1/12 width and the
                          label renders as "Comp…". */}
                      <div className={spans.third}>
                        <Form.Item
                          name={[name, 'mandatory']}
                          label="Compulsory"
                          valuePropName="checked"
                          initialValue={true}>
                          <Switch />
                        </Form.Item>
                      </div>
                      <div className={v2.rowDelete}>
                        <img
                          src={deleteIcon}
                          alt="Remove disclosure"
                          onClick={() => remove(name)}
                        />
                      </div>
                    </div>
                  </FadeEffect>
                ))}
                {/* New rows start compulsory — the field's own default, so the form and
                    the API agree without the operator having to think about it. */}
                <button
                  type="button"
                  onClick={() => add({mandatory: true})}
                  className={v2.addRow}>
                  <img src={plusIcon} alt="" />
                  <span>Add disclosure</span>
                </button>
              </div>
            )}
          </Form.List>
        </FormCard>

        <InstrumentActions
          onPrepopulate={() => {
            form.setFieldsValue({...KEY_FACTS_MOCK, ...DISCLOSURES_MOCK});
            onValuesChange();
          }}
          remaining={remaining}
          disabled={!pageOperations?.update}
          label="Save and proceed"
        />
      </Form>
    </div>
  );
};
